import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { ExchangeCard } from "@/components/exchange-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Link } from "react-router-dom";

// Exchange data
const dexExchanges = [
  {
    id: "dexonic",
    name: "Dexonic",
    logo: "/images/logos/dexonic-logo.webp",
    description: "Swap LRS with other tokens on Dexonic",
    links: [
      {
        pair: "LRS/USDT",
        url: "https://dexonic.xyz/#/swap?inputCurrency=0x13c378660cdda2afccea093f5e0743d5ec37a822&outputCurrency=0x2347302968d7d7cd25aa82ff8be78850ad8119f7"
      }
    ],
    type: "dex",
    icon: ""
  },
  {
    id: "uniswap",
    name: "Uniswap",
    logo: "/images/logos/larissa-uniswap.png",
    description: "Trade LRS on Uniswap (Polygon)",
    links: [
      {
        pair: "LRS/Any",
        url: "https://app.uniswap.org/swap?outputCurrency=0x6483DE4a2C76A38f288C4922fE2F507B2322eF80&chain=polygon"
      }
    ],
    type: "dex",
    icon: ""
  }
] as {
    id: string;
    name: string;
    logo: string;
    description: string;
    links: { pair: string; url: string }[];
    type: "dex" | "cex";
    icon: string;
}[];

const cexExchanges = [
  {
    id: "nonkyc",
    name: "Nonkyc",
    logo: "/images/logos/larissa-nonkyc.png",
    description: "Trade LRS on Nonkyc exchange",
    links: [
      {
        pair: "LRS/USDT",
        url: "https://nonkyc.io/market/LRS_USDT"
      }
    ],
    type: "cex",
    icon: ""
  }
] as {
    id: string;
    name: string;
    logo: string;
    description: string;
    links: { pair: string; url: string }[];
    type: "dex" | "cex";
    icon: string;
}[];

// Combine all exchanges
const allExchanges = [...dexExchanges, ...cexExchanges];

export default function BuyLRSPage() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Buy $LRS</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Buy $LRS</h1>
              </div>

              <p className="text-muted-foreground mb-8 max-w-3xl">
                Larissa ($LRS) is available on various centralized and decentralized exchanges.
                Choose your preferred platform to buy, sell, or trade LRS tokens.
              </p>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <Tabs defaultValue="all" className="mb-8">
                <TabsList className="mb-6">
                  <TabsTrigger value="all">All Exchanges</TabsTrigger>
                  <TabsTrigger value="dex">Decentralized (DEX)</TabsTrigger>
                  <TabsTrigger value="cex">Centralized (CEX)</TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {allExchanges.map(exchange => (
                      <ExchangeCard key={exchange.id} exchange={exchange} />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="dex">
                  <div className="flex items-center mb-4">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h2 className="text-2xl font-bold">Decentralized Exchanges (DEX)</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {dexExchanges.map(exchange => (
                      <ExchangeCard key={exchange.id} exchange={exchange} />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="cex">
                  <div className="flex items-center mb-4">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h2 className="text-2xl font-bold">Centralized Exchanges (CEX)</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {cexExchanges.map(exchange => (
                      <ExchangeCard key={exchange.id} exchange={exchange} />
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              <div className="mt-10 p-6 bg-sidebar border border-border rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-1 h-6 bg-[#FF8A3C] mr-3 inline-block"></span>
                  Important Information
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Always verify the contract address before trading</li>
                  <li>Be cautious of phishing attempts and fake exchanges</li>
                  <li>Consider using hardware wallets for long-term storage</li>
                  <li>Trading cryptocurrencies involves risk; only invest what you can afford to lose</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}





